import { Link } from "gatsby"
import React from "react"
import { Hidden } from "react-grid-system"
import styled, { css } from "styled-components"
import { ButtonLink } from "styleguide/components"
import { Container } from "styleguide/components/grid"
import { IconBurger, IconClose, IconGithubSmall } from "styleguide/icons"
import DocSidebar from "styleguide/page-components/DocArticle/DocSidebar"
import { uiColorToBaseColor } from "styleguide/styles/colors"
import { Articles, Location } from "../../../types/docs"

interface Props {
  repoName: string
  repoLink: string
  mdxName?: string
  currentSlug?: string
  categories?: string[]
  location?: Location
  articles?: Articles
}

const DocLink = styled(Link)`
  line-height: 24px;
  margin-left: 18px;
  display: block;
  padding-bottom: 20px;
  color: ${uiColorToBaseColor("darkestGray")};
  font-weight: 700;
  font-size: 16px;

  &:hover {
    color: ${uiColorToBaseColor("darkerRed")};
  }
`

const StyledMobileMenu = styled.div<{ isOpen: boolean }>`
  display: none;
  position: fixed;
  height: 100%;
  top: 86px;
  background: white;
  z-index: 100;
  width: 100%;
  left: 0;
  padding-top: 80px;
  padding-bottom: 20px;
  height: calc(100% - 86px);
  overflow: scroll;

  ${(props) =>
    props.isOpen &&
    css`
      display: block !important;
    `}
`

const StyledIconBurger = styled(IconBurger)`
  height: 24px;
  width: 24px;
  flex-shrink: 0;
`

const StyledIconClose = styled(IconClose)`
  height: 24px;
  width: 24px;
  flex-shrink: 0;
`

const StyledButtonLink = styled(ButtonLink)`
  background-color: ${uiColorToBaseColor("white")};
  color: ${uiColorToBaseColor("black")};
  display: flex;
  align-items: center;
  border: 1px solid ${uiColorToBaseColor("black")};
  font-size: 14px;
  line-height: 24px;
  padding: 5px 20px;
  white-space: nowrap;
  font-weight: 600;
  justify-content: center;
  margin-top: 24px;

  &:hover {
    background-color: ${uiColorToBaseColor("white")};
    color: ${uiColorToBaseColor("black")};
  }
`

const StyledIconGithubSmall = styled(IconGithubSmall)`
  max-width: 24px;
  width: 24px;
  margin-right: 12px;
  height: 25px;
  margin-top: 5px;
`

const SidebarSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${uiColorToBaseColor("lightGray")};
`

const SidebarTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${uiColorToBaseColor("gray")};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
  margin-left: 18px;
`

const MobileMenu = ({
  repoName,
  repoLink,
  mdxName,
  currentSlug,
  categories,
  location,
  articles,
}: Props) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const menuRef = React.useRef<HTMLDivElement>(null)

  const showMenu = () => {
    setIsOpen(true)
  }

  const closeMenu = () => {
    setIsOpen(false)
  }

  // Close menu when location changes (navigation occurs)
  // This handles ALL navigation - both pathname and hash changes
  React.useEffect(() => {
    setIsOpen(false)
  }, [location?.pathname, location?.hash])

  return (
    <>
      {!isOpen && <StyledIconBurger onClick={showMenu} />}
      {isOpen && <StyledIconClose onClick={closeMenu} />}
      <StyledMobileMenu ref={menuRef} isOpen={isOpen}>
        <Container>
          <DocLink to="/">ShakaCode Website</DocLink>
          <DocLink
            to="https://forum.shakacode.com/"
            target="_blank"
            rel="noopener"
          >
            Community
          </DocLink>
          {repoName.includes("rails") && (
            <DocLink to="/react-on-rails-pro/">React on Rails Pro</DocLink>
          )}

          <Hidden md>
            {repoLink && (
              <StyledButtonLink href={repoLink} target="_blank">
                <StyledIconGithubSmall color="black" />
                <span>View on Github</span>
              </StyledButtonLink>
            )}
          </Hidden>

          {mdxName && categories && articles && (
            <SidebarSection>
              <SidebarTitle>Documentation</SidebarTitle>
              <DocSidebar
                repoName={repoName}
                mdxName={mdxName}
                currentSlug={currentSlug}
                articles={articles}
                categories={categories}
                isMobileMenu
              />
            </SidebarSection>
          )}
        </Container>
      </StyledMobileMenu>
    </>
  )
}

export default MobileMenu
